import { useEffect, useState } from "react";
import { App, Button, Card, Flex, Radio, Space, Typography } from "antd";

import {
  EventContentMode,
  EventStatus,
  useUpdateEventContentModeMutation,
} from "api";
import { getErrorMessage } from "components";

interface Props {
  eventId: number;
  status: EventStatus;
  currentMode: EventContentMode;
}

interface ModeOption {
  value: EventContentMode;
  label: string;
  help: string;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: "mixed",
    label: "Text si media",
    help: "Participantii pot posta text, poze si videoclipuri, in orice combinatie. Este comportamentul implicit.",
  },
  {
    value: "media_only",
    label: "Doar foto / video",
    help: "Fiecare mesaj trebuie sa aiba un atasament. Textul e permis doar ca descriere langa poza sau videoclip — nu poate fi trimis singur.",
  },
  {
    value: "text_only",
    label: "Doar text",
    help: "Se pot trimite doar mesaje text. Butonul de camera dispare din aplicatie, iar pozele si videoclipurile sunt respinse.",
  },
];

/**
 * Content mode gets its own card instead of a field on the event form
 * because it is the only setting an operator may change after the event
 * has gone live — the general update endpoint refuses every edit past
 * `scheduled`. Saving it is a single, explicit action.
 *
 * The rule applies to everyone, hosts and moderators included, and is
 * never retroactive: messages already posted stay where they are.
 */
export const EventContentModeCard = ({
  eventId,
  status,
  currentMode,
}: Props) => {
  const { modal, notification } = App.useApp();
  const [draft, setDraft] = useState<EventContentMode>(currentMode);
  const [updateContentMode, { isLoading }] =
    useUpdateEventContentModeMutation();

  // Re-sync when the event refetches — otherwise a change made from
  // another tab would be silently overwritten by a stale draft.
  useEffect(() => {
    setDraft(currentMode);
  }, [currentMode]);

  const isArchived = status === "ended" || status === "purged";
  const isDirty = draft !== currentMode;

  const confirmAndSave = () => {
    const option = MODE_OPTIONS.find((o) => o.value === draft);

    modal.confirm({
      title: "Schimbi ce pot posta participantii?",
      okText: "Schimba",
      cancelText: "Renunta",
      width: 520,
      content: (
        <div>
          <Typography.Paragraph>
            Modul devine <strong>{option?.label}</strong>. {option?.help}
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {status === "live"
              ? "Evenimentul este in direct — schimbarea ajunge pe telefoanele participantilor in cateva secunde. Mesajele deja postate raman neatinse."
              : "Se aplica de la deschiderea chat-ului."}
          </Typography.Paragraph>
        </div>
      ),
      onOk: () =>
        updateContentMode({ id: eventId, contentMode: draft })
          .unwrap()
          .then(() => {
            notification.success({ message: "Modul de continut a fost salvat." });
          })
          .catch((e) => {
            notification.error({ message: getErrorMessage(e as never) });
          }),
    });
  };

  return (
    <Card title="Continut permis">
      <Typography.Paragraph type="secondary">
        Ce pot trimite participantii in chat-ul evenimentului. Regula se aplica
        tuturor, inclusiv gazdei si moderatorilor, si nu afecteaza retroactiv
        mesajele deja postate.
      </Typography.Paragraph>

      <Radio.Group
        value={draft}
        onChange={(e) => setDraft(e.target.value as EventContentMode)}
        disabled={isArchived || isLoading}
      >
        <Space direction="vertical" size={12}>
          {MODE_OPTIONS.map((option) => (
            <Radio key={option.value} value={option.value}>
              <Space direction="vertical" size={0}>
                <span style={{ fontWeight: 500 }}>{option.label}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {option.help}
                </Typography.Text>
              </Space>
            </Radio>
          ))}
        </Space>
      </Radio.Group>

      <Flex justify="flex-end" style={{ marginTop: 16 }}>
        <Button
          type="primary"
          loading={isLoading}
          disabled={isArchived || !isDirty}
          onClick={confirmAndSave}
        >
          Salveaza modul
        </Button>
      </Flex>

      {isArchived && (
        <Typography.Text type="secondary">
          Evenimentul s-a incheiat — chat-ul este arhivat si modul nu mai poate
          fi schimbat.
        </Typography.Text>
      )}
    </Card>
  );
};
